import React, { useEffect, useCallback, useState, useRef, Suspense, lazy } from 'react';
import * as Sentry from "@sentry/react";
import { MessageSquare, AlertCircle, Copy } from 'lucide-react';
import { Stage3D, CameraInfo } from './components/Stage3D';
import { SidebarTextToPose, SidebarJoints, SidebarCore, SidebarAttitude, SidebarReset, LibrarySaveButton, PoseSaveToFrameButton, PoseSourceStatus, PoseFolderStatus } from './components/Sidebar';
import { MainNavigation } from './components/MainNavigation';
import { BrandingOverlay } from './components/BrandingOverlay';
import { Timeline } from './components/Timeline';
import { ConfirmDialog } from './components/ConfirmDialog';
import { InputDialog } from './components/InputDialog';
import { SoundtrackModal } from './components/SoundtrackModal';
import { ShareDialog } from './components/ShareDialog';
import { useDanceStore } from './store/useDanceStore';
import { DanceState, Pose, ModuleType } from './types';
import { clamp } from './utils/constraints';
import * as THREE from 'three';
import { SequencesModule, PosesModule, SettingsModule, ProfileModule, ImportExportModule, InformationModule } from './components/Explorer';
import { useStorageStore, UNSORTED_SEQUENCES_ID } from './store/useStorageStore';
import { useAuthStore } from './store/useAuthStore';
import { TutorialModule } from './components/TutorialModule';
import { TutorialOverlay } from './components/TutorialOverlay';
import { TUTORIAL_MODULES } from './utils/tutorialData';
import { FeedbackDialog } from './components/FeedbackDialog';
import { UIConnections } from './components/UIConnections';
import { ContextMenu } from './components/ContextMenu';
import { useTranslation } from 'react-i18next';
import { useResponsive } from './contexts/ResponsiveContext';
import { SplashOverlay } from './components/SplashOverlay';
const ShowcaseSequencesModule = lazy(() => import('./components/ShowcaseSequencesModule').then(m => ({ default: m.ShowcaseSequencesModule })));
import { AuthModal } from './components/AuthModal';

import { trackEvent, trackTutorialEvent } from './lib/analytics';
import { fetchWithAuth } from './lib/api';


const Toast: React.FC = () => {
  const toastMessage = useDanceStore(s => s.toastMessage);
  const toastType = useDanceStore(s => s.toastType);
  if (!toastMessage) return null;
  const isError = toastType === 'error';
  return (
    <div className="toast-message" style={{
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(10px)',
      color: isError ? '#f87171' : '#5eead4',
      padding: '12px 24px',
      borderRadius: '12px',
      border: isError ? '1px solid rgba(248, 113, 113, 0.3)' : '1px solid rgba(94, 234, 212, 0.3)',
      boxShadow: isError ? '0 8px 16px rgba(0,0,0,0.5), 0 0 20px rgba(248, 113, 113, 0.1)' : '0 8px 16px rgba(0,0,0,0.5), 0 0 20px rgba(94, 234, 212, 0.1)',
      zIndex: 10000,
      fontWeight: 600,
      fontSize: '0.9rem',
      animation: 'app-toast-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
    }}>
      {toastMessage}
    </div>
  );
};
const TutorialTooltip: React.FC<{ onClose: (skipped?: boolean) => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [coords, setCoords] = React.useState<{ top: number, left: number } | null>(null);

  React.useEffect(() => {
    trackEvent('tutorial_tooltip_viewed');
  }, []);

  React.useEffect(() => {
    const updatePosition = () => {
      const el = document.getElementById('top-menu-tutorial');
      if (el) {
        const rect = el.getBoundingClientRect();
        setCoords({
          top: rect.top + rect.height / 2,
          left: rect.right + 12
        });
      }
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    const interval = setInterval(updatePosition, 100);
    return () => {
      window.removeEventListener('resize', updatePosition);
      clearInterval(interval);
    };
  }, []);

  if (!coords) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        transform: 'translateY(-50%)',
        background: 'rgba(56, 189, 248, 0.72)',
        backdropFilter: 'blur(8px)',
        border: 'none',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.6), 0 0 24px rgba(56, 189, 248, 0.3)',
        zIndex: 40000,
        maxWidth: '280px',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <div 
        style={{
          position: 'absolute',
          left: '-6px',
          top: '50%',
          transform: 'translateY(-50%) rotate(45deg)',
          width: '10px',
          height: '10px',
          background: 'rgba(56, 189, 248, 0.72)',
          borderLeft: 'none',
          borderBottom: 'none'
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
          {t('tutorial.onboarding_prompt_title', { defaultValue: 'Welcome to ChoreogrAIphy!' })}
        </h4>
        <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, color: 'rgba(15, 23, 42, 0.8)', lineHeight: 1.4, whiteSpace: 'pre-line' }}>
          {t('tutorial.onboarding_prompt_message', { defaultValue: 'Would you like a quick 1-minute interactive tour to learn the controls?\nYou can also take the tour later by using the Learn option in the main menu.' })}
        </p>
        <div style={{ display: 'flex', gap: '8px', marginTop: '4px', justifyContent: 'flex-end' }}>
          <button 
            onClick={() => {
              trackTutorialEvent('tooltip_prompt_skip', { name: 'onboarding_tooltip' });
              onClose(true);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(15, 23, 42, 0.5)',
              fontSize: '0.7rem',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '4px 8px'
            }}
          >
            {t('tutorial.skip', { defaultValue: 'Skip' })}
          </button>
          <button 
            onClick={() => {
              trackTutorialEvent('tooltip_prompt_start', { name: 'onboarding_tooltip' });
              const store = useDanceStore.getState();
              const storage = useStorageStore.getState();
              store.newSequence();
              store.setDuration(3.5);
              storage.updateTutorialProgress({
                currentModuleId: 'introduction',
                currentStepIndex: 0
              });
              store.setActiveModule('PoseDesigner');
              onClose();
            }}
            style={{
              background: '#0f172a',
              border: 'none',
              borderRadius: '4px',
              color: '#38bdf8',
              fontSize: '0.7rem',
              fontWeight: 700,
              cursor: 'pointer',
              padding: '4px 10px'
            }}
          >
            {t('tutorial.start_tour', { defaultValue: 'Start Tour' })}
          </button>
        </div>
      </div>
    </div>
  );
};

const FeedbackNudgeTooltip: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [coords, setCoords] = React.useState<{ anchorY: number, left: number } | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    trackEvent('feedback_nudge_tooltip_viewed');
  }, []);

  React.useEffect(() => {
    const handleInteraction = (e: MouseEvent | TouchEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [onClose]);

  React.useEffect(() => {
    const updatePosition = () => {
      const el = document.getElementById('nav-feedback');
      if (el) {
        const rect = el.getBoundingClientRect();
        setCoords({
          anchorY: rect.top + rect.height / 2,
          left: rect.right + 12
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    const interval = setInterval(updatePosition, 100);
    return () => {
      window.removeEventListener('resize', updatePosition);
      clearInterval(interval);
    };
  }, []);

  if (!coords) return null;

  const tooltipHeight = tooltipRef.current?.offsetHeight ?? 0;
  const margin = 8;
  const rawTop = coords.anchorY - tooltipHeight / 2;
  const clampedTop = Math.min(rawTop, window.innerHeight - tooltipHeight - margin);
  const caretTop = coords.anchorY - clampedTop;

  return (
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: `${clampedTop}px`,
        left: `${coords.left}px`,
        background: 'rgba(94, 234, 212, 0.72)',
        backdropFilter: 'blur(8px)',
        border: 'none',
        borderRadius: '12px',
        padding: '16px 20px',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.6), 0 0 24px rgba(94, 234, 212, 0.3)',
        zIndex: 40000,
        maxWidth: '280px',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '-6px',
          top: `${caretTop - 5}px`,
          transform: 'rotate(45deg)',
          width: '10px',
          height: '10px',
          background: 'rgba(94, 234, 212, 0.72)',
          borderLeft: 'none',
          borderBottom: 'none'
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
          {t('feedback.nudge_title', { defaultValue: 'Your feedback matters!' })}
        </h4>
        <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, color: 'rgba(15, 23, 42, 0.8)', lineHeight: 1.4 }}>
          {t('feedback.nudge_message', { defaultValue: 'Help shape ChoreogrAIphy — share what you think, what you love, or what you\'d like to see next.' })}
        </p>
      </div>
    </div>
  );
};

export default function App() {
  const { t } = useTranslation();
  const { isMobile: isMobileRaw, isTouch, isLandscapeMobile } = useResponsive();
  const isMobile = isMobileRaw || isTouch;
  const setReadOnly = useDanceStore(state => state.setReadOnly);
  const setTimelineZoom = useDanceStore(state => state.setTimelineZoom);

  const [layoutKey, setLayoutKey] = useState(0);
  useEffect(() => {
    const onOrientationChange = () => {
      setLayoutKey(k => k + 1);
      const orientation = screen.orientation?.type?.startsWith('landscape') ||
        window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
      trackEvent('device_orientation_changed', {
        to: orientation,
        active_module: useDanceStore.getState().activeModule,
      });
    };
    window.addEventListener('orientationchange', onOrientationChange);
    screen.orientation?.addEventListener('change', onOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', onOrientationChange);
      screen.orientation?.removeEventListener('change', onOrientationChange);
    };
  }, []);

  const user = useAuthStore(state => state.user);
  const loadedSequence = useDanceStore(state => state.loadedSequence);
  const [showAuth, setShowAuth] = useState(false);
  const pendingSharedSequenceId = useRef<string | null>(null);

  const authLoading = useAuthStore(state => state.loading);
  const loadSequence = useDanceStore(state => state.loadSequence);
  const setActiveModule = useDanceStore(state => state.setActiveModule);
  const setToastMessage = useDanceStore(state => state.setToastMessage);

  useEffect(() => {
    const isSharedReadOnly = loadedSequence && (
      (loadedSequence.userId && loadedSequence.userId !== user?.uid) ||
      (user?.email && loadedSequence.collaborators?.includes(user.email))
    );
    const isRO = isMobile || !!isSharedReadOnly;
    setReadOnly(isRO);
    if (isRO) {
      setTimelineZoom(0.7);
    } else {
      setTimelineZoom(1.0);
    }
  }, [isMobile, setReadOnly, setTimelineZoom, loadedSequence, user]);

  // Handle URL deep-linking on mount/load
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (!path.startsWith('/seqid=')) return;

    const targetSeqId = path.split('/seqid=')[1];
    if (!targetSeqId) return;

    // Wait until auth loading is finished
    if (authLoading) return;

    let isMounted = true;
    const checkAndLoadSequence = async () => {
      try {
        // If user is logged in (and not a guest/anonymous), try fetching the sequence
        if (user && !user.isAnonymous) {
          try {
            const seqData = await fetchWithAuth(`/api/sequences/${targetSeqId}`);
            if (isMounted) {
              loadSequence(seqData);
              setActiveModule('PoseDesigner');
              window.history.replaceState({}, '', '/');
              setToastMessage(t('sharing.loaded_shared_sequence', 'Loaded shared sequence!'));
            }
          } catch (err: any) {
            console.error('Failed to fetch shared sequence:', err);
            if (isMounted) {
              if (err.status === 403) {
                setToastMessage(t('sharing.unauthorized_access', 'You do not have access to this shared sequence. Please sign in with the email address it was shared with.'), 'error');
                pendingSharedSequenceId.current = targetSeqId;
                setShowAuth(true);
              } else {
                setToastMessage(t('sharing.failed_load_shared', 'Failed to load shared sequence. It may have been deleted or you no longer have access.'), 'error');
              }
              window.history.replaceState({}, '', '/');
            }
          }
        } else {
          // User is not logged in or is anonymous guest, request login/signup
          if (isMounted) {
            setToastMessage(t('sharing.please_signin', 'Please sign in or sign up to view this shared sequence.'));
            pendingSharedSequenceId.current = targetSeqId;
            setShowAuth(true);
            window.history.replaceState({}, '', '/');
          }
        }
      } catch (err) {
        console.error('Deep link check failed:', err);
        if (isMounted) {
          setToastMessage(t('sharing.failed_load_shared', 'Failed to load shared sequence. It may have been deleted or you no longer have access.'), 'error');
          window.history.replaceState({}, '', '/');
        }
      }
    };

    checkAndLoadSequence();

    return () => {
      isMounted = false;
    };
  }, [authLoading, user, loadSequence, setActiveModule, setToastMessage, t]);

  // Effect to load the pending sequence once auth is complete
  useEffect(() => {
    if (authLoading || !user || user.isAnonymous || !pendingSharedSequenceId.current) return;

    const seqId = pendingSharedSequenceId.current;
    pendingSharedSequenceId.current = null; // Clear immediately

    let isMounted = true;
    const fetchAndLoad = async () => {
      try {
        const seqData = await fetchWithAuth(`/api/sequences/${seqId}`);
        if (isMounted) {
          loadSequence(seqData);
          setActiveModule('PoseDesigner');
          setToastMessage(t('sharing.loaded_shared_sequence', 'Loaded shared sequence!'));
        }
      } catch (err) {
        console.error('Failed to load shared sequence after login:', err);
        if (isMounted) {
          setToastMessage(t('sharing.failed_load_shared', 'Failed to load shared sequence. It may have been deleted or you no longer have access.'), 'error');
        }
      }
    };

    fetchAndLoad();

    return () => {
      isMounted = false;
    };
  }, [user, authLoading, loadSequence, setActiveModule, setToastMessage, t]);

  console.log("App Rendering...");
  // Disable default browser context menu globally
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    window.addEventListener('contextmenu', handleContextMenu);
    return () => window.removeEventListener('contextmenu', handleContextMenu);
  }, []);


  const showAiFeatures = useStorageStore(state => state.preferences.showAiFeatures);
  const tutorialProgress = useStorageStore(state => state.tutorialProgress);
  const updateTutorialProgress = useStorageStore(state => state.updateTutorialProgress);
  const anonymousTouVersion = useStorageStore(state => state.anonymousTouVersion);
  const anonymousPrivacyVersion = useStorageStore(state => state.anonymousPrivacyVersion);
  const [showSplash, setShowSplash] = React.useState(false);
  const [showTutorialTooltip, setShowTutorialTooltip] = React.useState(false);
  const [showFeedbackNudge, setShowFeedbackNudge] = React.useState(false);

  // Show/hide splash based on user login status and anonymous policy acceptance.
  // Policy-update enforcement for anonymous users is handled separately
  // by TermsUpdateModal, which blocks the UI when anonymousTouUpdateRequired
  // or anonymousPrivacyUpdateRequired is true.
  useEffect(() => {
    const hasAcceptedTermsAnonymously = anonymousTouVersion !== null && anonymousPrivacyVersion !== null;

    if (authLoading) {
      // Prevent showing the splash screen while loading authentication state.
      // This eliminates the flashing effect for signed-in users on reload.
      setShowSplash(false);
    } else if (user || hasAcceptedTermsAnonymously) {
      // Bypass if signed in OR if the anonymous user has already accepted terms and entered the app in a prior session
      setShowSplash(false);
    } else {
      // Otherwise, show splash screen to prompt for consent/onboarding
      setShowSplash(true);
    }
  }, [user, authLoading, anonymousTouVersion, anonymousPrivacyVersion]);
  const confirmAction = useDanceStore(state => state.confirmAction);
  const activeModule = useDanceStore((state: DanceState) => state.activeModule);

  const setDuration = useDanceStore((state: DanceState) => state.setDuration);
  const keyframes = useDanceStore((state: DanceState) => state.keyframes);
  const currentTime = useDanceStore((state: DanceState) => state.currentTime);
  const isPlaying = useDanceStore((state: DanceState) => state.isPlaying);
  const hasUnsavedSequenceChanges = useDanceStore((state: DanceState) => state.hasUnsavedSequenceChanges);
  const hasUnsavedChanges = useDanceStore((state: DanceState) => state.hasUnsavedChanges);
  
  const actingSource = useDanceStore((state: DanceState) => state.actingSource);
  const setIsFeedbackOpen = useDanceStore((state: DanceState) => state.setIsFeedbackOpen);
  const contextMenu = useDanceStore((state: DanceState) => state.contextMenu);
  const closeContextMenu = useDanceStore((state: DanceState) => state.closeContextMenu);

  const duplicateSequence = useStorageStore(state => state.duplicateSequence);
  const isReadOnly = useDanceStore(state => state.isReadOnly);

  // Warn the user before closing the window/tab if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges || hasUnsavedSequenceChanges) {
        e.preventDefault();
        e.returnValue = ''; // Required for standard browser confirmation behavior
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, hasUnsavedSequenceChanges]);

  useEffect(() => {
    // Only interpolate if we are in timeline mode or haven't started editing (Scratch)
    if (actingSource.type !== 'timeline' && actingSource.type !== 'none') return;
    
    const keyframesWithPose = keyframes.filter(kf => !!kf.pose);
    if (keyframesWithPose.length === 0) return;

    if (keyframesWithPose.length === 1) {
      // If only one keyframe with pose, stay on that pose
      const singlePose = keyframesWithPose[0].pose!;
      useDanceStore.setState({ currentPose: singlePose });
      return;
    }

    // Find the immediately preceding and succeeding keyframes containing a pose
    let prevKf = keyframesWithPose[0];
    let nextKf = keyframesWithPose[keyframesWithPose.length - 1];
    
    // If before first keyframe with pose
    if (currentTime <= prevKf.time) {
      useDanceStore.setState({ currentPose: prevKf.pose! });
      return;
    }
    // If after last keyframe with pose
    if (currentTime >= nextKf.time) {
       useDanceStore.setState({ currentPose: nextKf.pose! });
       return;
    }

    for (let i = 0; i < keyframesWithPose.length - 1; i++) {
      if (currentTime >= keyframesWithPose[i].time && currentTime < keyframesWithPose[i+1].time) {
        prevKf = keyframesWithPose[i];
        nextKf = keyframesWithPose[i+1];
        break;
      }
    }

    // Calculate lerp factor (0 to 1) 
    const timeSpan = nextKf.time - prevKf.time;
    const timeProgress = currentTime - prevKf.time;
    const factor = clamp(timeProgress / timeSpan, 0, 1);

    // Interpolate every joint
    const interpolatedPose = {} as Pose;
    const jointNames = Object.keys(prevKf.pose!);
    
    jointNames.forEach(joint => {
      const p1 = prevKf.pose![joint];
      const p2 = nextKf.pose![joint] || p1; // Fallback if missing

      if (joint === 'notes') {
        interpolatedPose.notes = p1 as string;
        return;
      }

      // Lerp X, Y, Z for the joint if they are valid numeric arrays
      if (Array.isArray(p1) && Array.isArray(p2) && p1.length >= 3 && p2.length >= 3) {
        interpolatedPose[joint] = [
          THREE.MathUtils.lerp(p1[0] as number, p2[0] as number, factor),
          THREE.MathUtils.lerp(p1[1] as number, p2[1] as number, factor),
          THREE.MathUtils.lerp(p1[2] as number, p2[2] as number, factor)
        ];
      } else {
        interpolatedPose[joint] = p1 as any;
      }
    });

    useDanceStore.setState({ currentPose: interpolatedPose });

  }, [currentTime, keyframes, isPlaying, actingSource]); // Update pose on scrub, tick, or source change
  
  // Auto-switch to Pose Designer when playback starts from an explorer module
  useEffect(() => {
    if (isPlaying && (activeModule === 'Sequences' || activeModule === 'Poses')) {
      setActiveModule('PoseDesigner');
    }
  }, [isPlaying, activeModule, setActiveModule]);

  // Ensure mobile users are not stuck in invalid tabs like Tutorial
  useEffect(() => {
    if (isMobile && activeModule === 'Tutorial') {
      setActiveModule('PoseDesigner');
    }
  }, [isMobile, activeModule, setActiveModule]);


  const handleModuleChange = (mod: ModuleType) => {
    if (activeModule === mod) return;

    // Check if the current tutorial step requires switching to this specific module/tab
    const currentModule = TUTORIAL_MODULES.find(m => m.id === tutorialProgress.currentModuleId);
    const currentStep = currentModule?.steps[tutorialProgress.currentStepIndex];
    const isSwitchingRequiredTab = currentStep?.actionRequired === 'switchTab' && currentStep?.targetValue === mod;

    if (tutorialProgress.currentModuleId && !tutorialProgress.completedModuleIds.includes(tutorialProgress.currentModuleId) && !isSwitchingRequiredTab) {
      confirmAction({
        title: t('app.abandon_tutorial_title'),
        message: t('app.abandon_tutorial_message'),
        onConfirm: () => {
          updateTutorialProgress({ currentModuleId: null, currentStepIndex: 0 });
          setDuration(10);
          setActiveModule(mod);
        }
      });
    } else {
      setActiveModule(mod);
    }
  };

  return (
    <div className="app-container">
      {isReadOnly && activeModule === 'PoseDesigner' && loadedSequence && (
        (loadedSequence.userId && loadedSequence.userId !== user?.uid) ||
        (user?.email && loadedSequence.collaborators?.includes(user.email))
      ) && (
        <div className="read-only-banner glass-panel">
          <div className="read-only-banner-left">
            <AlertCircle size={14} style={{ color: '#fbbf24', flexShrink: 0 }} />
            <span className="read-only-short-text">{t('sharing.read_only_simple', 'Read Only')}</span>
            <span className="read-only-full-text">{t('sharing.read_only_warning', 'Read-Only Mode: You are viewing a shared sequence.')}</span>
          </div>
        </div>
      )}
      <div className="canvas-container">
        <Stage3D />
      </div>
      
      <div key={layoutKey} style={{ display: 'contents' }}>
      {/* Conditionally render Sidebars for Pose Designer */}
      {activeModule === 'PoseDesigner' && !isMobile && (
        <>
          <div className="sidebar-stack right">
            <SidebarTextToPose />
            <SidebarJoints />
            <SidebarReset />
          </div>
          <div className="sidebar-stack">
            <div className="flex-row" style={{ gap: '5px', alignItems: 'flex-start', pointerEvents: 'none' }}>
              <div className="flex-col" style={{ gap: '5px', alignItems: 'stretch', pointerEvents: 'none' }}>
                <SidebarCore />
                <SidebarAttitude />
                <CameraInfo />
              </div>
              <div className="flex-col" style={{ gap: '5px', alignItems: 'stretch', pointerEvents: 'none' }}>
                <PoseFolderStatus />
                <LibrarySaveButton />
                <PoseSourceStatus />
                <PoseSaveToFrameButton />
              </div>
            </div>
            <UIConnections />
          </div>
        </>
      )}

      {/* Conditionally render Explorers */}
      {activeModule === 'Sequences' && <SequencesModule />}
      {activeModule === 'Poses' && <PosesModule />}
      {activeModule === 'Tutorial' && <TutorialModule />}
      {activeModule === 'Settings' && <SettingsModule />}
      {activeModule === 'Profile' && <ProfileModule />}
      {activeModule === 'ShowcaseSequences' && <Suspense fallback={null}><ShowcaseSequencesModule /></Suspense>}
      {activeModule === 'ImportExport' && <ImportExportModule />}
      {activeModule === 'Information' && <InformationModule />}

      {activeModule !== 'Sequences' && activeModule !== 'Tutorial' && activeModule !== 'Settings' && activeModule !== 'ShowcaseSequences' && activeModule !== 'Profile' && activeModule !== 'ImportExport' && activeModule !== 'Information' && !(isMobile && activeModule === 'Poses') && <Timeline />}
      
      {showSplash && (
        <SplashOverlay
          onClose={(showTooltip) => {
            setShowSplash(false);
            if (showTooltip) {
              setShowTutorialTooltip(true);
            }
          }}
        />
      )}
      {showTutorialTooltip && (
        <TutorialTooltip onClose={(skipped) => {
          setShowTutorialTooltip(false);
          if (skipped) setShowFeedbackNudge(true);
        }} />
      )}
      {showFeedbackNudge && <FeedbackNudgeTooltip onClose={() => setShowFeedbackNudge(false)} />}
      
      <MainNavigation />
      <BrandingOverlay />
      </div>
      
      <ConfirmDialog />
      <InputDialog />
      <ShareDialog />
      <SoundtrackModal />
      <TutorialOverlay />
      <FeedbackDialog />
      <Toast />
      
      {contextMenu && contextMenu.isOpen && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={closeContextMenu}
          align={contextMenu.align}
        />
      )}

      {authLoading && (
        <div className="app-loading-overlay">
          <div className="app-loading-spinner" />
          <span className="app-loading-label">{t('app.loading_library')}</span>
        </div>
      )}
      {showAuth && (
        <AuthModal onClose={() => setShowAuth(false)} />
      )}
    </div>
  );
}


